<template>
  <div class="space-y-32">
    <div>
      <h1 class="heading-1">{{ i18n(translations.title) }}</h1>
      <p class="mt-8">{{ i18n(translations.description) }}</p>
      <p v-if="data" class="mt-32" data-test="return-deadline">
        {{
          i18n(translations.returnDeadlineReminder, {
            date: i18n.date(
              new Date(data.initialOrder.returnWindow),
              DATE_FORMAT_RETURN_DEADLINE,
            ),
          })
        }}
      </p>
    </div>
    <div>
      <h2 class="heading-3 mb-16">
        {{ i18n(translations.replacementOrderTitle) }}
      </h2>
      <XrReplacementOrderSummary
        v-if="data"
        data-qa="replacement-order-summary"
        data-test="replacement-order-summary"
        :delivery-dates-window="data.replacementOrder.deliveryDatesWindow"
        :image="data.replacementOrder.product.image.href"
        :title="data.replacementOrder.product.title"
      />
    </div>
    <div>
      <h2 class="heading-3 mb-16">{{ i18n(translations.paymentFormTitle) }}</h2>
      <RevInfoBlock
        class="mb-16"
        :icon="IconCreditCard"
        :title="i18n(translations.paymentFormInfoBlockTitle)"
        variant="info"
      >
        <p class="text-static-default-mid body-2">
          {{ i18n(translations.paymentFormInfoBlockContent) }}
        </p>
        <RevLink class="body-2-bold" @click="goToExpressReplacementTC">{{
          i18n(translations.paymentFormInfoBlockLink)
        }}</RevLink>
      </RevInfoBlock>
      <XrPaymentForm />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  navigateTo,
  useAsyncData,
  useRoute,
  useRouteLocationWithLocale,
} from '#imports'
import { inject, onMounted } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevLink } from '@ds/components/Link'
import { IconCreditCard } from '@ds/icons/IconCreditCard'

import { CMS } from '~/scopes/cms/routes-names'
import XrPaymentForm from '~/scopes/customer-care/resolution/components/ExpressReplacement/payment/XrPaymentForm/XrPaymentForm.vue'
import XrReplacementOrderSummary from '~/scopes/customer-care/resolution/components/ExpressReplacement/payment/XrReplacementOrderSummary/XrReplacementOrderSummary.vue'
import { ResolutionFlowStateMachineKey } from '~/scopes/customer-care/resolution/symbols'
import { LEGAL_PAGE_NAME } from '~/scopes/legal/constants'

import { DATE_FORMAT_RETURN_DEADLINE } from './ExpressReplacementPayment.constants'
import translations from './ExpressReplacementPayment.translations'

const resolutionFlowStateMachine = inject(ResolutionFlowStateMachineKey)
const $httpFetch = useHttpFetch()
const resolveLocalizedRoute = useRouteLocationWithLocale()
const i18n = useI18n()
const { params } = useRoute()
const getXrDataKey = getAsyncDataKey(
  'express-replacement-payment-get-xr-data',
  {
    resolutionId: params.resolutionId,
  },
)

const { data, error } = await useAsyncData(getXrDataKey, () =>
  $httpFetch('/express-replacement/funnel/resolutions/:resolutionId', {
    pathParams: {
      resolutionId: String(params.resolutionId),
    },
  }),
)

const goToExpressReplacementTC = async () => {
  await navigateTo(
    resolveLocalizedRoute({
      name: CMS.LEGAL_PAGE,
      params: {
        pageName: LEGAL_PAGE_NAME.TERMS_EXPRESS_REPLACEMENT,
      },
    }) as RouteLocationRaw,
    { open: { target: '_blank' } },
  )
}

resolutionFlowStateMachine?.send('GO_TO_PAYMENT')

onMounted(() => {
  if (error.value) {
    resolutionFlowStateMachine?.send('ERROR')
  }
})
</script>
